import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, IconButton, Paper } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useMutation } from '@apollo/client';
import { InvoicingState, Restoration, RestorationAction, RestorationMaterial, SubOrderLineKind } from 'models/order';
import { CurrencyContext } from 'context/currency';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { removeRestorationRowTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import TypographyWithHighLighting from 'components/with-hith-light-components/typography-with-highlighting.component';
import { isTioTandvardRestoration } from 'components/order-flow/shared/restoration-input-helper';
import ActionsDataGridWrapper from '../constructions-section/actions/actions-data-grid-wrapper.component';
import MaterialsDataGridWrapper from '../constructions-section/materials/materials-data-grid-wrapper.component';
import ScannerCaseInfo from '../constructions-section/scanner-case-info/scanner-case-info.component';
import RestorationProperties from './restoration-properties.component';
import RestorationActionsTable from './restoration-actions-table.component';
import RestorationMaterialsTable from './restoration-materials-table.component';
import { formatRestorationTitle } from './restoration-format';
import SubOrderLinesTable from './sub-order-lines-table.component';

type RestorationCardProps = {
    index: number;
    restoration: Restoration;
    originalRestoration?: Restoration;
    versionCheck: boolean;
    redirectToModifyOrder: (isResend: boolean) => void;
    isExternalTechnician: boolean;
    isCustomer: boolean;
    isSupplier: boolean;
};

type RowToRemove = { kind: 'action' | 'material'; row: RestorationAction | RestorationMaterial };

/**
 * One restoration: title, properties, actions and materials. The supplier edits the rows
 * of every order shape in the action and material grids (DD-2506 step 3); teeth and
 * properties are edited in the order flow.
 */
const RestorationCard: FC<RestorationCardProps> = ({
    index,
    restoration,
    originalRestoration,
    versionCheck,
    redirectToModifyOrder,
    isExternalTechnician,
    isCustomer,
    isSupplier,
}) => {
    const { t: translate } = useTranslation([
        'orders',
        'common',
        'construction-props',
        'restoration-types',
        'actions-materials',
    ]);
    const { order, refetch } = useContext(OrderDetailsContext);
    const { currency } = useContext(CurrencyContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    const [editActions, setEditActions] = useState(false);
    const [editMaterials, setEditMaterials] = useState(false);
    const [rowToRemove, setRowToRemove] = useState<RowToRemove>();

    useEffect(() => {
        // Leaving the order, or switching version, closes any open grid.
        return () => {
            setEditActions(false);
            setEditMaterials(false);
        };
    }, [versionCheck, order?._id]);

    const [removeRestorationRow, { loading: removing }] = useMutation(removeRestorationRowTemplate, {
        onCompleted: () => {
            setRowToRemove(undefined);
            refetch();
        },
        onError(error) {
            setRowToRemove(undefined);
            showBoundary(error);
        },
    });

    const isRestrictedExternalUser = permissions.isRestrictedExternalUser(currentUser?.role);
    const isLabCreated = Boolean(order.isCreatedByLab);
    const modifyLocked = order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    // DD-3391: the elevated tier does a technician's production work on the orders
    // shared with them — adding actions and materials. The backend enforces both the
    // tier and the order linkage; this is only so the buttons match.
    const canEdit =
        versionCheck &&
        !modifyLocked &&
        (!isExternalTechnician || permissions.isElevatedExternalUser(currentUser?.role));
    const showPrices = !isRestrictedExternalUser;
    const scannerCase = order.scannerCase?._id ? order.scannerCase : undefined;
    const hasScannerCase = Boolean(scannerCase);
    // Only the supplier's technicians price and change the rows, on the current version.
    const canEditRows = canEdit && isSupplier && permissions.isLabTechnician(currentUser.type);
    const nonSurchargeMaterials = restoration.materials.filter((row) => !row.isMaterialSurcharge);
    // DD-3486: the sub-orders' rows for this restoration, read-only (sending lab only).
    const subOrderLines = (order.subOrderLines ?? []).filter((line) => line.restorationId === restoration._id);

    const confirmRemove = () => {
        if (!rowToRemove) return;
        removeRestorationRow({
            variables: { orderId: order._id, restorationId: restoration._id, rowId: rowToRemove.row._id },
        });
    };

    return (
        <Box mb={4}>
            <Box mb={1} display="flex" alignItems="flex-end">
                <TypographyWithHighLighting
                    data={formatRestorationTitle(restoration, index, translate, !hasScannerCase)}
                    originalData={formatRestorationTitle(originalRestoration, index, translate, !hasScannerCase)}
                    variant="h3"
                />
                {isTioTandvardRestoration(restoration) ? (
                    <Chip
                        label={translate('orders:TIOTANDVARD')}
                        size="small"
                        sx={{ ml: 1, mb: 0.5 }}
                        data-test={`restoration-card.tiotandvard-chip.${index}`}
                    />
                ) : undefined}
                {canEdit && !hasScannerCase && (!isLabCreated || !isCustomer) ? (
                    <Box>
                        <IconButton
                            onClick={() => redirectToModifyOrder(false)}
                            size="small"
                            color="primary"
                            data-test={`restoration-card.modify-construction-icon-btn.${index}`}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : undefined}
            </Box>
            {canEdit && hasScannerCase && (!isLabCreated || !isCustomer) ? (
                <Box>
                    <Button
                        onClick={() => redirectToModifyOrder(false)}
                        data-test={`restoration-card.modify-construction-btn.${index}`}
                    >
                        {translate(isLabCreated ? 'orders:MODIFY_CONSTRUCTION' : 'orders:MODIFY_ACTIONS')}
                    </Button>
                </Box>
            ) : undefined}

            <RestorationProperties restoration={restoration} originalRestoration={originalRestoration} />

            <Box mb={2}>
                {canEditRows && editActions ? (
                    <Paper sx={{ padding: 2 }} variant="outlined">
                        <ActionsDataGridWrapper
                            restoration={restoration}
                            onEditComplete={() => {
                                setEditActions(false);
                                refetch();
                            }}
                            onMaterialsPersisted={refetch}
                        />
                    </Paper>
                ) : (
                    <RestorationActionsTable
                        rows={restoration.actions}
                        originalRows={originalRestoration?.actions ?? []}
                        currency={currency}
                        showPrices={showPrices}
                        onEditRow={canEditRows ? () => setEditActions(true) : undefined}
                        onRemoveRow={canEditRows ? (row) => setRowToRemove({ kind: 'action', row }) : undefined}
                        dataTestPrefix={`restoration-card.${index}`}
                    />
                )}
                <SubOrderLinesTable
                    lines={subOrderLines}
                    kind={SubOrderLineKind.ACTION}
                    showPrices={showPrices}
                    dataTestPrefix={`restoration-card.${index}.sub-order-lines`}
                />
            </Box>

            {canEditRows && !editActions ? (
                <Box mb={2}>
                    <Button
                        size="small"
                        onClick={() => setEditActions(true)}
                        data-test={`restoration-card.edit-actions-btn.${index}`}
                    >
                        {translate('orders:EDIT_ACTIONS')}
                    </Button>
                </Box>
            ) : undefined}

            <Box mb={1}>
                {canEditRows && editMaterials ? (
                    <Paper sx={{ padding: 2 }} variant="outlined">
                        <MaterialsDataGridWrapper
                            restoration={restoration}
                            onEditComplete={() => {
                                setEditMaterials(false);
                                refetch();
                            }}
                        />
                    </Paper>
                ) : (
                    <RestorationMaterialsTable
                        rows={restoration.materials}
                        originalRows={originalRestoration?.materials ?? []}
                        currency={currency}
                        showPrices={showPrices}
                        onEditRow={canEditRows ? () => setEditMaterials(true) : undefined}
                        onRemoveRow={
                            canEditRows
                                ? (row) => {
                                      if (!row.isMaterialSurcharge) setRowToRemove({ kind: 'material', row });
                                  }
                                : undefined
                        }
                        disableDelete={nonSurchargeMaterials.length === 1 && !restoration.actions.length}
                        dataTestPrefix={`restoration-card.${index}`}
                    />
                )}
                <SubOrderLinesTable
                    lines={subOrderLines}
                    kind={SubOrderLineKind.MATERIAL}
                    showPrices={showPrices}
                    dataTestPrefix={`restoration-card.${index}.sub-order-lines`}
                />
            </Box>

            {canEditRows && !editMaterials ? (
                <Box>
                    <Button
                        size="small"
                        onClick={() => setEditMaterials(true)}
                        data-test={`restoration-card.edit-materials-btn.${index}`}
                    >
                        {translate('orders:EDIT_MATERIALS')}
                    </Button>
                </Box>
            ) : undefined}

            {scannerCase ? (
                <Paper variant="outlined" sx={{ padding: 2, marginTop: 2 }}>
                    <ScannerCaseInfo scannerCase={scannerCase} />
                </Paper>
            ) : undefined}

            <ConfirmDialog
                open={Boolean(rowToRemove)}
                handleClose={() => (removing ? undefined : setRowToRemove(undefined))}
                handleConfirm={confirmRemove}
                dialogTitle={translate(
                    rowToRemove?.kind === 'action'
                        ? 'actions-materials:DELETE_ACTION'
                        : 'actions-materials:DELETE_MATERIAL',
                )}
                dialogContent={translate(
                    rowToRemove?.kind === 'action'
                        ? 'actions-materials:CONFIRM_DELETE_ACTION'
                        : 'actions-materials:CONFIRM_DELETE_MATERIAL',
                )}
                confirmBtnLabel={translate('common:REMOVE')}
            />
        </Box>
    );
};

export default RestorationCard;
